/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

export type WatcherState = 'success' | 'failure' | 'pending' | 'missing';

export type WatcherStatus = {
  state: WatcherState;
  description: string;
  updatedAt: string | null;
};

export type WatcherPr = {
  owner: string;
  repo: string;
  number: number;
  title: string;
  url: string;
  author: string;
  authorUrl: string;
  authorAvatar: string | null;
  headSha: string | null;
  committedAt: string | null;
  createdAt: string;
  status: WatcherStatus;
};

export type WatcherList = {
  orgs: string[];
  repos: string[];
  reconcileEnabled: boolean;
  reconcileMinutes: number;
  lastSweepAt: number | null;
  builtAt: number;
  prs: WatcherPr[];
  truncated: boolean;
};

export type ActionResult = {
  status: WatcherStatus & { momentary: boolean };
};

// Module-level cache so the sidebar access probe and the page share one
// round-trip (same convention as useNetworkPolicyAccess / useTeamAdminAccess).
type Access = { reachable: boolean | undefined; canWrite: boolean };
let cached: Access | undefined;
let inflight: Promise<Access> | undefined;

export const useDependsOnWatcherAccess = (): Access => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<Access>(
    cached ?? { reachable: undefined, canWrite: false },
  );

  useEffect(() => {
    if (cached !== undefined) {
      setState(cached);
      return undefined;
    }
    let active = true;
    inflight =
      inflight ??
      (async () => {
        try {
          const base = await discoveryApi.getBaseUrl('depends-on-watcher');
          const res = await fetchApi.fetch(`${base}/access`);
          if (!res.ok) return { reachable: false, canWrite: false };
          const json = await res.json().catch(() => ({}));
          return { reachable: true, canWrite: json.canWrite === true };
        } catch {
          return { reachable: false, canWrite: false };
        }
      })();
    inflight.then(result => {
      // Cache only a definitive grant. Caching a deny would defeat the backend's
      // grant-only canWrite cache: a permitted user who probed mid-ingestion
      // (canWrite:false) would stay actionless for the whole SPA session.
      if (result.reachable && result.canWrite) cached = result;
      else inflight = undefined; // unreachable OR transient deny: re-probe next mount
      if (active) setState(result);
    });
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  return state;
};

export const useDependsOnWatcherApi = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const base = await discoveryApi.getBaseUrl('depends-on-watcher');
      const res = await fetchApi.fetch(`${base}${path}`, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      return body;
    },
    [discoveryApi, fetchApi],
  );

  // Normal load serves the instant in-memory snapshot; refresh=true forces a
  // fresh build server-side (the "Refresh" button).
  const listPrs = useCallback(
    (refresh = false): Promise<WatcherList> =>
      call(`/prs${refresh ? '?refresh=1' : ''}`),
    [call],
  );

  const recheck = useCallback(
    (owner: string, repo: string, number: number): Promise<ActionResult> =>
      call(
        `/prs/${encodeURIComponent(owner)}/${encodeURIComponent(
          repo,
        )}/${number}/recheck`,
        { method: 'POST' },
      ),
    [call],
  );

  const unblock = useCallback(
    (owner: string, repo: string, number: number): Promise<ActionResult> =>
      call(
        `/prs/${encodeURIComponent(owner)}/${encodeURIComponent(
          repo,
        )}/${number}/unblock`,
        { method: 'POST' },
      ),
    [call],
  );

  // Stable identity so consumers can hang effects off it without re-firing.
  return useMemo(() => ({ listPrs, recheck, unblock }), [listPrs, recheck, unblock]);
};
